<template>
    <component
        :is="'li'"
        class="CreateHceMediaButton"
    >
        <button
            :disabled="isDisabled"
            class="CreateHceMediaButton-link"
            @click="onShowCreateHceMediaPresentationClick"
        >
            <span class="CreateHceMediaButton-iconWrapper">
                <icomoon-icon
                    class="CreateHceMediaButton-icon"
                    name="Phone"
                />
            </span>
            <span class="CreateHceMediaButton-label">
                <strong class="CreateHceMediaButton-title">
                    {{ $t('hce:create_media:label') }}
                </strong>
                <span class="CreateHceMediaButton-subtitle">
                    {{ $t('hce:create_media:sublabel') }}
                </span>
            </span>
            <v-chip
                v-if="showNewFeatureOnCreateHceButton"
                class="CreateHceMediaButton-newFeature"
                color="info"
                size="small"
                variant="tonal"
            >
                {{ $t('miscellaneous:new') }}
            </v-chip>
        </button>
        <modal
            v-model="showCreateHceModal"
            :title="$t('hce:create_media:modal:title')"
            :width="'full'"
        >
            <div class="CreateHceMediaButton-content">
                <v-alert
                    v-if="createHceError"
                    class="CreateHceMediaButton-error"
                    text
                    type="error"
                    variant="tonal"
                >
                    {{ createHceError }}
                </v-alert>
                <transition name="fade">
                    <div
                        v-show="showCreatingHceMediaLoader"
                        class="CreateHceMediaButton-creationInProgress"
                    >
                        <v-progress-circular
                            indeterminate
                            color="primary"
                        />
                        <p class="CreateHceMediaButton-creationInProgressTitle">
                            {{ $t('hce:create_media:modal:creation_in_progress:title') }}
                        </p>
                        <p class="CreateHceMediaButton-creationInProgressDescription">
                            {{ $t('hce:create_media:modal:creation_in_progress:description') }}
                        </p>
                    </div>
                </transition>
                <transition name="fade">
                    <create-hce-media-presentation
                        v-show="!showCreatingHceMediaLoader"
                        class="CreateHceMediaButton-presentation"
                    />
                </transition>
                <v-btn
                    :disabled="showCreatingHceMediaLoader"
                    class="CreateHceMediaButton-modalButton"
                    color="primary"
                    size="x-large"
                    variant="flat"
                    @click="onCreateHceMediaClick"
                >
                    {{ $t('hce:create_media:modal:button_text') }}
                </v-btn>
            </div>
        </modal>
    </component>
</template>

<script setup>
import { computed, ref } from 'vue'
import { createHceMedia } from '@/StoreWeb/js/mixins/wallet-utils'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import IcomoonIcon from '@/StoreWeb/components/common/IcomoonIcon.vue'
import config from 'config'
import * as internalActionTypes from '@/StoreWeb/store/modules/application-session/internal-action-types'
import * as externalActionTypes from '@/StoreWeb/store/modules/application-session/external-action-types'
import * as applicationSessionMutationTypes from '@/StoreWeb/store/modules/application-session/mutation-types'
import * as applicationSessionActionTypes from '@/StoreWeb/store/modules/application-session/action-types'
import { isEmpty } from 'global-utils'
import AddToCartFlowManager from '@/StoreWeb/managers/AddToCartFlowManagerSingleton'

const store = useStore()
const router = useRouter()

const props = defineProps({
    provider: {
        type: String,
        default: ''
    }
})

const addToCartFlowManager = ref(AddToCartFlowManager.getInstance())
const createHceError = ref('')
const showCreateHceModal = ref(false)
const showCreateHceMediaPresentation = ref(false)
const showCreatingHceMediaLoader = ref(false)

const applicationSession = computed(() => store.state.applicationSessionModule.applicationSession)
const isDisabled = computed(() => store.state.userModule.userPendingRequests.getWallet)
const isMobileApp = computed(() => store.state.isMobileApp)
const providerConfig = computed(() => config.providers.find(provider => provider.id === props.provider))
const showNewFeatureOnCreateHceButton = computed(() => providerConfig.value?.hce?.display_new_feature)

async function onShowCreateHceMediaPresentationClick () {
    showCreateHceMediaPresentation.value = true
    showCreateHceModal.value = true
}

const finalizeHceMedia = async (temporaryFareMediaId) => {
    addToCartFlowManager.value.setAddToCartFlowStateToLocalStorage()

    if (isMobileApp.value) {
        const baseUrl = `${window.location.origin}/internalLinks/${internalActionTypes.GO_TO_CATALOG}?sessionId=${applicationSession.value.id}&initialRequest=${externalActionTypes.CREATE_DEMATERIALIZED_FARE_MEDIA}`

        applicationSession.value.data.addToCartFlow = localStorage.getItem('addToCartFlow')
        applicationSession.value.data.temporaryFareMediaId = temporaryFareMediaId
        applicationSession.value.sessionContext = {
            currentStep: baseUrl,
            resumeStepSuccess: `${baseUrl}&status=success`,
            resumeStepError: `${baseUrl}&status=error`
        }

        store.commit(applicationSessionMutationTypes.SET_APPLICATION_SESSION, applicationSession.value)
        await store.dispatch(applicationSessionActionTypes.UPDATE_APPLICATION_SESSION)

        const externalLink = router.resolve({
            name: 'externalLinks',
            params: { action: externalActionTypes.CREATE_DEMATERIALIZED_FARE_MEDIA },
            query: { sessionId: applicationSession.value.id }
        })

        if (!isEmpty(externalLink)) {
            window.location.href = window.location.origin + externalLink.href
        }
    }
}

async function onCreateHceMediaClick () {
    showCreateHceMediaPresentation.value = false
    showCreatingHceMediaLoader.value = true
    createHceError.value = ''

    const response = await createHceMedia(props.provider)

    if (response?.error) {
        showCreatingHceMediaLoader.value = false
        createHceError.value = response.error?.message

        return
    }

    showCreateHceModal.value = false
    showCreatingHceMediaLoader.value = false

    if (response?.temporaryFareMediaId) {
        await finalizeHceMedia(response.temporaryFareMediaId)
    }
}
</script>

<style lang="scss" scoped>
@import 'globalScss';

.CreateHceMediaButton {
    margin-bottom: 15px;

    &:last-child {
        margin-bottom: 0;
    }

    &-icon {
        font-size: 26px;
        color: $color-brandSecondary;
        transition: color .3s ease-in-out;

        &--active {
            color: $color-white;
        }
    }

    &-link {
        display: flex;
        width: 100%;
        padding: $s3;
        border-radius: $border_radius_medium;
        box-shadow: 0 0 8px 0 rgba(0, 0, 0, .2);
        background-color: $color-lightest;
        cursor: pointer;
        text-align: left;
        color: $color-defaultText;
        transition: background-color .3s ease-in-out;

        &:disabled {
            cursor: not-allowed;
        }

        &:hover:not([disabled]) {
            background-color: $color-brandSecondary;
            color: $color-white;

            .CreateHceMediaButton-icon {
                color: $color-white;
            }
        }
    }

    &-iconWrapper {
        @include verticalCenter;

        justify-content: center;
        width: 40px;
        height: 40px;
        margin-right: $margin-xsmall;
        border-radius: $borderRadius-rounded;
        background: rgba(red($color-brandSecondary), green($color-brandSecondary), blue($color-brandSecondary), .1);
        text-align: center;
        transition: background .3s ease-in-out;
    }

    &:first-child {
        padding-top: 0;
        border-top: none;
    }

    &-label {
        flex-grow: 1;
        line-height: 1.3rem;
    }

    &-title {
        display: block;
        font-family: $fontFamily-title;
        font-size: $font-small;
        font-weight: $fontWeight-titleBold;
    }

    &-subtitle {
        font-size: 12px;
        font-weight: $fontWeight-defaultMedium;
        line-height: 0;
    }

    &-newFeature {
        font-weight: $font_weight_default_medium;
    }

    &-content {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        gap: $s8;
        height: 100%;
    }

    &-error {
        flex: 0 0 auto;
    }

    &-presentation,
    &-creationInProgress {
        flex-grow: 1;
    }

    &-creationInProgress {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: $s8;
    }

    &-creationInProgressTitle {
        margin: 0;
        font-size: $font-medium;
        font-weight: $fontWeight-defaultBold;
    }

    &-creationInProgressDescription {
        margin: 0;
        font-size: $font-small;
    }

    &-modalButton {
        align-self: stretch;
    }
}

.fade-enter-active {
    transition: opacity .3s ease-in-out;
}

.fade-enter {
    opacity: 0;
}

.fade-leave-active {
    transition: none;
}

.fade-leave-to {
    opacity: 1;
}
</style>
